import React, { useEffect, useRef } from 'react';
import dataVisualizationImage from '../assets/data-visualization.webp';
import realTimeTrackingImage from '../assets/realtime-tracking.webp';
import historicalDataImage from '../assets/historical-data.webp';
import explorerApiImage from '../assets/explore-api.webp';
import AnimatedSVG from "../components/AnimatedSVG"; 
import '../css/Explorer.css';

const AIDePINExplorer = () => {
  const featureRefs = useRef([]);
  const svgRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      },
      { threshold: 0.1 }
    );

    featureRefs.current.forEach((ref) => {
      if (ref) {
        observer.observe(ref);
      }
    });

    return () => {
      featureRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  const handleMouseEnter = (index) => {
    const svgElement = svgRefs.current[index];
    if (svgElement) {
      svgElement.classList.add('animate-path');
    }
  };

  const handleMouseLeave = (index) => {
    const svgElement = svgRefs.current[index];
    if (svgElement) {
      svgElement.classList.remove('animate-path');
    }
  };

  return (
    <section id="ai-depin-explorer" className="explorer-section">
      <h2>AI-DePIN Explorer: Explore the Network</h2>

      <div className="explorer-features">
        <div className="feature-explorer-row">
          <div
            className="feature-explorer-item slide-left"
            ref={(el) => (featureRefs.current[0] = el)}
            onMouseEnter={() => handleMouseEnter(0)}
            onMouseLeave={() => handleMouseLeave(0)}
          >
            <div className="feature-explorer-background">
              <AnimatedSVG ref={(el) => (svgRefs.current[0] = el)} />
            </div>
            <div className="feature-explorer-icons">
              <img src={dataVisualizationImage} alt="Data Visualization" className="feature-explorer-image" />
              <h3>Real-time Network Monitoring</h3>
            </div>
            <div className="feature-explorer-content">
              <p>Visualize up-to-the-minute network data through detailed, interactive graphs that display performance metrics. Monitor both GPU clusters and mining operations simultaneously to track the flow of tasks, processing power, and overall network health.</p>
              <p>This real-time monitoring is essential for ensuring your operations are running efficiently and without interruption.</p>
            </div>
          </div>

          <div
            className="feature-explorer-item slide-right reverse"
            ref={(el) => (featureRefs.current[1] = el)}
            onMouseEnter={() => handleMouseEnter(1)}
            onMouseLeave={() => handleMouseLeave(1)}
          >
            <div className="feature-explorer-background">
              <AnimatedSVG ref={(el) => (svgRefs.current[1] = el)} />
            </div>
            <div className="feature-explorer-icons">
              <h3>Historical Data Analysis</h3>
              <img src={realTimeTrackingImage} alt="Real-Time Tracking" className="feature-explorer-image" />
            </div>
            <div className="feature-explorer-content">
              <p>Analyze historical data to track network performance trends over time. Compare past metrics like block generation, resource usage, and task completions to identify patterns and optimize future operations.</p>
              <p>The historical data feature helps businesses and startups make data-driven decisions by providing insights into the efficiency and reliability of their deployed GPU clusters and mining operations.</p>
            </div>
          </div>
        </div>

        <div className="feature-explorer-row">
          <div
            className="feature-explorer-item slide-left"
            ref={(el) => (featureRefs.current[2] = el)}
            onMouseEnter={() => handleMouseEnter(2)}
            onMouseLeave={() => handleMouseLeave(2)}
          >
            <div className="feature-explorer-background">
              <AnimatedSVG ref={(el) => (svgRefs.current[2] = el)} />
            </div>
            <div className="feature-explorer-icons">
              <img src={historicalDataImage} alt="Historical Data" className="feature-explorer-image" />
              <h3>Enhanced Transparency and Reporting</h3>
            </div>
            <div className="feature-explorer-content">
              <p>With AI-DePIN Explorer, you gain a transparent view of all operations, whether it’s for AI task assignments or mining activities. The reporting tools offer detailed breakdowns of tasks completed, processing power used, and earnings generated.</p>
              <p>This visibility empowers users to fine-tune their strategies and maximize their computational efficiency and earnings potential.</p>
            </div>
          </div>

          <div
            className="feature-explorer-item slide-right reverse"
            ref={(el) => (featureRefs.current[3] = el)}
            onMouseEnter={() => handleMouseEnter(3)}
            onMouseLeave={() => handleMouseLeave(3)}
          >
            <div className="feature-explorer-background">
              <AnimatedSVG ref={(el) => (svgRefs.current[3] = el)} />
            </div>
            <div className="feature-explorer-icons">
              <h3>API Access and Integration</h3>
              <img src={explorerApiImage} alt="Explorer API" className="feature-explorer-image" />
            </div>
            <div className="feature-explorer-content">
              <p>The AI-DePIN Explorer offers powerful API access, allowing providers to seamlessly integrate real-time data on GPU clusters and mining operations into their own systems.</p>
              <p>This feature enables automated monitoring, performance tracking, and custom dashboards to help optimize network efficiency and resource allocation.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIDePINExplorer;
